import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { formatDurationMinutes } from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { TimeEntryEditModal } from "@/pages/employees/time-tracking/widgets/TimeEntryEditModal.tsx";
import { ColumnSizes } from "@/types/common.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiTimesheetRow } from "@/types/time-tracking.types.ts";
import {
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Table,
  TableCell,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TimesheetsTableCell } from "./TimesheetsTableCell.tsx";

interface TimesheetsTableProps {
  rows: ApiTimesheetRow[];
  dates: Date[];
  isLoading: boolean;
}

export const TimesheetsTable = ({ rows, dates, isLoading }: TimesheetsTableProps) => {
  const { t } = useTranslation();
  const { canCurrentEmployeeEditField } = usePermissions();
  const navigate = useNavigate();
  const { openModal } = useModals();

  const columnHelper = createColumnHelper<ApiTimesheetRow>();

  const columns = useMemo<ColumnDef<ApiTimesheetRow, unknown>[]>(() => {
    const cols: ColumnDef<ApiTimesheetRow, unknown>[] = [
      columnHelper.display({
        id: "employee",
        header: t("core:employee"),
        cell: (cell) => (
          <EmployeeHoverCard
            employee={cell.row.original.employee}
            position={getEmployeePositionTitle(cell.row.original.employee)}
          >
            <EmployeeItem employee={cell.row.original.employee} enableLink showPosition={true} />
          </EmployeeHoverCard>
        ),
        ...ColumnSizes.employee,
      }),
      columnHelper.display({
        id: "total",
        header: t("common:total"),
        ...ColumnSizes.standard,
        cell: ({ row }) => (
          <Text className={"font-semibold tabular-nums text-default"}>
            {formatDurationMinutes(row.original.total_minutes)}
          </Text>
        ),
      }),
      columnHelper.display({
        id: "active_session",
        header: t("clocking:active_session"),
        ...ColumnSizes.wide,
        cell: ({ row }) => {
          const active = row.original.active_entry;
          const canEdit = canCurrentEmployeeEditField(EmployeeFieldPermission.clocking_entries);
          if (!active) {
            return <TableCell variant={"secondary"}>–</TableCell>;
          }
          const activeLabel = t("clocking:active_session_since", {
            date: format(active.datetime_clocked_in, "MMM d, HH:mm"),
          });
          if (!canEdit) {
            return <Text className={"text-warning"}>{activeLabel}</Text>;
          }
          return (
            <button
              onClick={() => openModal(TimeEntryEditModal, { entry: active })}
              className={
                "flex items-center gap-(--gap-xs) text-warning cursor-pointer hover:underline text-left"
              }
            >
              <FontAwesomeIcon icon={"fa-light fa-circle-play"} />
              {activeLabel}
            </button>
          );
        },
      }),
      ...dates.map((date) =>
        columnHelper.display({
          id: `day-${date.toISOString()}`,
          header: format(date, "MMM d, EEE"),
          ...ColumnSizes.standard,
          cell: ({ row }) => {
            const employee = row.original.employee;
            const day = row.original.days.find(
              (d) => d.date_day.toDateString() === date.toDateString()
            );
            if (!day) return <Text>–</Text>;

            const canEdit = canCurrentEmployeeEditField(EmployeeFieldPermission.clocking_entries);
            return <TimesheetsTableCell day={day} employee={employee} canEdit={canEdit} />;
          },
        })
      ),
    ];

    return cols;
  }, [rows, dates, t, openModal]);

  const renderRowAction = useCallback(
    (row: Row<ApiTimesheetRow>) => {
      const emp = row.original.employee;
      const canEdit = canCurrentEmployeeEditField(EmployeeFieldPermission.clocking_entries);

      return (
        <DropdownMenu>
          {canEdit && (
            <DropdownMenuItem
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
              title={t("clocking:create_entry")}
              onClick={() => {
                openModal(TimeEntryEditModal, {
                  entry: null,
                  employeeId: emp.id,
                  date: dates[0],
                });
              }}
            />
          )}
          <DropdownMenuItem
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-user"} />}
            title={t("clocking:view_profile")}
            onClick={() => navigate(`/employees/${emp.id}`)}
          />
        </DropdownMenu>
      );
    },
    [canCurrentEmployeeEditField, dates, navigate, openModal, t]
  );

  return (
    <Table
      data={rows}
      columns={columns}
      renderRowAction={renderRowAction}
      showSkeleton={isLoading}
      stickyHeader={true}
      stickyFirstColumn={true}
    />
  );
};
